import React, { useState, useRef, useCallback } from "react";
import { supabase } from "./supabaseClient";

const SUPABASE_ANON_KEY = "sb_publishable_gsHORs-OWooYG4WzhAIcHw_5D81FM84";

const PRO_FIGHTERS = [
  { id: "muhammad_ali", name: "Muhammad Ali", style: "Orthodox", era: "1960s-70s", trait: "Float like a butterfly" },
  { id: "mike_tyson", name: "Mike Tyson", style: "Peek-a-Boo", era: "1980s-90s", trait: "Baddest man on the planet" },
  { id: "floyd_mayweather", name: "Floyd Mayweather Jr", style: "Philly Shell", era: "1990s-2010s", trait: "50-0 undefeated" },
  { id: "manny_pacquiao", name: "Manny Pacquiao", style: "Southpaw", era: "2000s-2010s", trait: "8-division world champion" },
  { id: "lomachenko", name: "Vasyl Lomachenko", style: "Orthodox", era: "2010s-present", trait: "The Matrix" },
  { id: "canelo", name: "Canelo Alvarez", style: "Orthodox", era: "2010s-present", trait: "Pound for pound king" },
  { id: "sugar_ray_leonard", name: "Sugar Ray Leonard", style: "Orthodox", era: "1970s-80s", trait: "Speed and flash" },
  { id: "tyson_fury", name: "Tyson Fury", style: "Orthodox", era: "2010s-present", trait: "The Gypsy King" },
  { id: "usyk", name: "Oleksandr Usyk", style: "Southpaw", era: "2010s-present", trait: "Undisputed champion" },
  { id: "pernell_whitaker", name: "Pernell Whitaker", style: "Southpaw", era: "1980s-90s", trait: "Sweet Pea" },
  { id: "marvin_hagler", name: "Marvin Hagler", style: "Southpaw", era: "1970s-80s", trait: "Marvelous" },
  { id: "bernard_hopkins", name: "Bernard Hopkins", style: "Orthodox", era: "1990s-2010s", trait: "The Executioner" },
  { id: "ggg", name: "Gennady Golovkin", style: "Orthodox", era: "2010s-present", trait: "Big Drama Show" },
  { id: "willie_pep", name: "Willie Pep", style: "Orthodox", era: "1940s-50s", trait: "Will o the Wisp" },
  { id: "sugar_ray_robinson", name: "Sugar Ray Robinson", style: "Orthodox", era: "1940s-50s", trait: "Pound for pound GOAT" },
];

function extractFrames(videoFile, numFrames) {
  return new Promise(function(resolve, reject) {
    var video = document.createElement("video");
    var canvas = document.createElement("canvas");
    var ctx = canvas.getContext("2d");
    var frames = [];
    video.preload = "auto";
    video.muted = true;
    video.src = URL.createObjectURL(videoFile);
    video.onloadedmetadata = function() {
      canvas.width = 640;
      canvas.height = 360;
      var duration = video.duration;
      var interval = duration / (numFrames + 1);
      var captureFrame = function(time) {
        return new Promise(function(res) {
          video.currentTime = time;
          video.onseeked = function() {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            var base64 = canvas.toDataURL("image/jpeg", 0.7).split(",")[1];
            frames.push(base64);
            res();
          };
        });
      };
      var captureAll = async function() {
        for (var i = 1; i <= numFrames; i++) {
          await captureFrame(interval * i);
        }
        URL.revokeObjectURL(video.src);
        resolve(frames);
      };
      captureAll().catch(reject);
    };
    video.onerror = reject;
  });
}

function StyleComparison({ user }) {
  const [selectedFighter, setSelectedFighter] = useState(null);
  const [video, setVideo] = useState(null);
  const [style, setStyle] = useState("orthodox");
  const [analyzing, setAnalyzing] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [error, setError] = useState(null);
  const [progressMsg, setProgressMsg] = useState("");
  const [mode, setMode] = useState("upload");
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [cameraReady, setCameraReady] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [search, setSearch] = useState("");

  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const boxingStyles = [
    { value: "orthodox", label: "Orthodox" },
    { value: "southpaw", label: "Southpaw" },
    { value: "philly_shell", label: "Philly Shell" },
    { value: "peek_a_boo", label: "Peek-a-Boo" },
    { value: "soviet", label: "Soviet/Kronk" },
    { value: "swarmer", label: "Swarmer" },
  ];

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); }
      setCameraReady(true);
      setError(null);
    } catch (err) {
      setError("Camera access denied. Please allow camera access.");
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(function(t) { t.stop(); }); streamRef.current = null; }
    setCameraReady(false);
    setRecording(false);
    setRecordingTime(0);
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
      setRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, []);

  const startRecording = useCallback(() => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    var mediaRecorder;
    try { mediaRecorder = new MediaRecorder(streamRef.current, { mimeType: "video/webm" }); }
    catch(e) { mediaRecorder = new MediaRecorder(streamRef.current); }
    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.ondataavailable = function(e) { if (e.data.size > 0) chunksRef.current.push(e.data); };
    mediaRecorder.onstop = function() {
      var blob = new Blob(chunksRef.current, { type: "video/webm" });
      var file = new File([blob], "recording-" + Date.now() + ".webm", { type: "video/webm" });
      setPreviewUrl(URL.createObjectURL(blob));
      setVideo(file);
      stopCamera();
    };
    mediaRecorder.start();
    setRecording(true);
    setRecordingTime(0);
    timerRef.current = setInterval(function() {
      setRecordingTime(function(prev) { if (prev >= 59) { stopRecording(); return prev; } return prev + 1; });
    }, 1000);
  }, [stopCamera, stopRecording]);

  const switchMode = useCallback(function(newMode) {
    stopCamera();
    setMode(newMode);
    setVideo(null);
    setPreviewUrl(null);
    if (newMode === "record") setTimeout(function() { startCamera(); }, 300);
  }, [stopCamera, startCamera]);

  const handleAnalyze = async () => {
    if (!video || !selectedFighter) return;
    setAnalyzing(true);
    setError(null);
    setFeedback(null);
    try {
      setProgressMsg("Uploading video...");
      const fileName = user.id + "/compare_" + Date.now() + "_" + video.name;
      await supabase.storage.from("videos").upload(fileName, video);
      setProgressMsg("Extracting frames...");
      const frames = await extractFrames(video, 5);
      setProgressMsg("Comparing your technique to " + selectedFighter.name + "...");
      const response = await fetch("https://ybcwbttitirdwbjtitsk.supabase.co/functions/v1/ai-feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + SUPABASE_ANON_KEY },
        body: JSON.stringify({ type: "style_comparison", frames, style, proFighter: selectedFighter.id })
      });
      const data = await response.json();
      if (data.feedback) { setFeedback(data.feedback); setProgressMsg(""); }
      else throw new Error(data.error || "No feedback received");
    } catch (err) { setError(err.message); setProgressMsg(""); }
    setAnalyzing(false);
  };

  const filteredFighters = PRO_FIGHTERS.filter(f =>
    f.name.toLowerCase().includes(search.toLowerCase()) ||
    f.style.toLowerCase().includes(search.toLowerCase())
  );

  const getMatchPercentage = (text) => {
    const match = text.match(/STYLE MATCH[:\s]+(\d+)%/);
    return match ? parseInt(match[1]) : null;
  };

  return (
    <div style={{fontFamily:"Inter, sans-serif"}}>
      <style>{`
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes pulse-ring { 0%{transform:scale(1);opacity:1} 100%{transform:scale(1.8);opacity:0} }
      `}</style>

      <h2 style={{fontFamily:"Bebas Neue, sans-serif",fontSize:"2.5rem",letterSpacing:"2px",background:"linear-gradient(135deg, #e94560, #ff6b35)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",marginBottom:"8px",marginTop:0}}>STYLE COMPARISON</h2>
      <p style={{color:"#555",marginBottom:"25px",fontSize:"0.9rem"}}>Upload your video and compare your technique to a boxing legend</p>

      {!feedback ? (
        <>
          <div style={{marginBottom:"25px"}}>
            <label style={{display:"block",marginBottom:"12px",color:"#888",fontSize:"0.8rem",textTransform:"uppercase",letterSpacing:"1px"}}>Choose Your Boxing Idol</label>
            <input
              type="text"
              placeholder="Search fighters..."
              value={search}
              onChange={function(e){ setSearch(e.target.value); }}
              style={{width:"100%",padding:"12px",backgroundColor:"rgba(255,255,255,0.04)",color:"white",border:"1px solid rgba(255,255,255,0.08)",borderRadius:"10px",fontSize:"0.9rem",fontFamily:"Inter, sans-serif",boxSizing:"border-box",outline:"none",marginBottom:"12px"}}
            />
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(200px, 1fr))",gap:"10px",maxHeight:"320px",overflowY:"auto",paddingRight:"5px"}}>
              {filteredFighters.map(function(fighter) {
                const isSelected = selectedFighter?.id === fighter.id;
                return (
                  <div key={fighter.id} onClick={function(){ setSelectedFighter(fighter); }} style={{padding:"14px",borderRadius:"12px",cursor:"pointer",border: isSelected ? "1px solid rgba(233,69,96,0.6)" : "1px solid rgba(255,255,255,0.06)",background: isSelected ? "linear-gradient(135deg, rgba(233,69,96,0.15), rgba(255,107,53,0.08))" : "rgba(255,255,255,0.02)",transition:"all 0.2s"}}>
                    <p style={{color: isSelected ? "#e94560" : "white",margin:0,fontWeight:"700",fontSize:"0.9rem"}}>{fighter.name}</p>
                    <p style={{color:"#555",margin:"3px 0 0 0",fontSize:"0.75rem"}}>{fighter.style} • {fighter.era}</p>
                    <p style={{color:"#444",margin:"3px 0 0 0",fontSize:"0.72rem",fontStyle:"italic"}}>{fighter.trait}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {selectedFighter && (
            <>
              <div style={{background:"linear-gradient(135deg, rgba(233,69,96,0.1), rgba(255,107,53,0.05))",border:"1px solid rgba(233,69,96,0.2)",borderRadius:"14px",padding:"16px 20px",marginBottom:"20px",display:"flex",alignItems:"center",gap:"12px"}}>
                <span style={{fontSize:"1.5rem"}}>🥊</span>
                <div>
                  <p style={{color:"white",margin:0,fontWeight:"700"}}>{selectedFighter.name}</p>
                  <p style={{color:"#666",margin:"2px 0 0 0",fontSize:"0.8rem"}}>{selectedFighter.style} • {selectedFighter.trait}</p>
                </div>
              </div>

              <div style={{background:"rgba(255,255,255,0.02)",padding:"16px",borderRadius:"14px",marginBottom:"20px",border:"1px solid rgba(255,255,255,0.06)"}}>
                <label style={{display:"block",marginBottom:"10px",color:"#888",fontSize:"0.8rem",textTransform:"uppercase",letterSpacing:"1px"}}>Your Boxing Style</label>
                <div style={{display:"flex",gap:"8px",flexWrap:"wrap"}}>
                  {boxingStyles.map(function(s) {
                    return (
                      <button key={s.value} onClick={function(){ setStyle(s.value); }} style={{padding:"8px 16px",borderRadius:"8px",cursor:"pointer",fontFamily:"Inter, sans-serif",fontSize:"0.85rem",fontWeight:"600",border: style===s.value ? "none" : "1px solid rgba(255,255,255,0.08)",background: style===s.value ? "linear-gradient(135deg, #e94560, #ff6b35)" : "rgba(255,255,255,0.03)",color: style===s.value ? "white" : "#666"}}>
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{display:"flex",gap:"8px",marginBottom:"15px"}}>
                <button onClick={function(){ switchMode("upload"); }} style={{flex:1,padding:"12px",borderRadius:"10px",cursor:"pointer",fontFamily:"Inter, sans-serif",fontSize:"0.9rem",fontWeight:"700",border: mode==="upload" ? "none" : "1px solid rgba(255,255,255,0.08)",background: mode==="upload" ? "linear-gradient(135deg, #e94560, #ff6b35)" : "rgba(255,255,255,0.03)",color: mode==="upload" ? "white" : "#666"}}>
                  📁 Upload Video
                </button>
                <button onClick={function(){ switchMode("record"); }} style={{flex:1,padding:"12px",borderRadius:"10px",cursor:"pointer",fontFamily:"Inter, sans-serif",fontSize:"0.9rem",fontWeight:"700",border: mode==="record" ? "none" : "1px solid rgba(255,255,255,0.08)",background: mode==="record" ? "linear-gradient(135deg, #e94560, #ff6b35)" : "rgba(255,255,255,0.03)",color: mode==="record" ? "white" : "#666"}}>
                  🎥 Record Live
                </button>
              </div>

              {mode === "upload" && (
                <div style={{background:"rgba(255,255,255,0.02)",padding:"20px",borderRadius:"14px",border:"1px solid rgba(255,255,255,0.06)",marginBottom:"15px"}}>
                  {!video ? (
                    <div style={{border:"2px dashed rgba(255,255,255,0.1)",borderRadius:"10px",padding:"30px",textAlign:"center",position:"relative",cursor:"pointer"}}>
                      <input type="file" accept="video/*" onChange={function(e){ setVideo(e.target.files[0]); }} style={{position:"absolute",inset:0,opacity:0,cursor:"pointer",width:"100%",height:"100%"}} />
                      <div style={{fontSize:"2.5rem",marginBottom:"10px"}}>📹</div>
                      <p style={{color:"#666",margin:0,fontSize:"0.9rem"}}>Click to upload your training video</p>
                      <p style={{color:"#444",margin:"6px 0 0 0",fontSize:"0.75rem"}}>Max 50MB • 30 seconds recommended</p>
                    </div>
                  ) : (
                    <div style={{textAlign:"center"}}>
                      <div style={{fontSize:"2rem",marginBottom:"8px"}}>✅</div>
                      <p style={{color:"#e94560",margin:0,fontWeight:"600"}}>{video.name}</p>
                      <button onClick={function(){ setVideo(null); }} style={{marginTop:"10px",padding:"6px 14px",background:"transparent",color:"#555",border:"1px solid rgba(255,255,255,0.08)",borderRadius:"8px",cursor:"pointer",fontFamily:"Inter, sans-serif",fontSize:"0.8rem"}}>Change video</button>
                    </div>
                  )}
                </div>
              )}

              {mode === "record" && (
                <div style={{background:"rgba(255,255,255,0.02)",padding:"20px",borderRadius:"14px",border:"1px solid rgba(255,255,255,0.06)",marginBottom:"15px"}}>
                  {!video ? (
                    <>
                      <div style={{position:"relative",borderRadius:"10px",overflow:"hidden",marginBottom:"15px",background:"#000",aspectRatio:"16/9"}}>
                        <video ref={videoRef} muted playsInline style={{width:"100%",height:"100%",objectFit:"cover"}} />
                        {!cameraReady && <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center"}}><p style={{color:"#666"}}>Camera loading...</p></div>}
                        {recording && (
                          <div style={{position:"absolute",top:"10px",left:"10px",display:"flex",alignItems:"center",gap:"6px",background:"rgba(0,0,0,0.7)",borderRadius:"20px",padding:"5px 12px"}}>
                            <div style={{width:"7px",height:"7px",borderRadius:"50%",background:"#e94560",animation:"blink 1s ease-in-out infinite"}}></div>
                            <span style={{color:"white",fontSize:"0.8rem",fontWeight:"600"}}>{recordingTime}s</span>
                          </div>
                        )}
                      </div>
                      <div style={{textAlign:"center"}}>
                        <div style={{position:"relative",display:"inline-block"}}>
                          {recording && <div style={{position:"absolute",top:"-8px",left:"-8px",right:"-8px",bottom:"-8px",borderRadius:"50%",border:"3px solid rgba(233,69,96,0.4)",animation:"pulse-ring 1.5s ease-out infinite"}}></div>}
                          <button onClick={function(){ recording ? stopRecording() : startRecording(); }} disabled={!cameraReady} style={{width:"70px",height:"70px",borderRadius:"50%",background: recording ? "linear-gradient(135deg, #e94560, #ff6b35)" : cameraReady ? "rgba(233,69,96,0.15)" : "rgba(255,255,255,0.05)",border: recording ? "none" : "3px solid " + (cameraReady ? "#e94560" : "#333"),cursor: cameraReady ? "pointer" : "not-allowed",fontSize:"1.8rem",display:"flex",alignItems:"center",justifyContent:"center"}}>
                            {recording ? "⏹" : "⏺"}
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div>
                      <video src={previewUrl} controls style={{width:"100%",borderRadius:"10px",marginBottom:"10px",maxHeight:"250px"}} />
                      <button onClick={function(){ setVideo(null); setPreviewUrl(null); startCamera(); }} style={{width:"100%",padding:"10px",background:"rgba(255,255,255,0.04)",color:"#666",border:"1px solid rgba(255,255,255,0.08)",borderRadius:"8px",cursor:"pointer",fontFamily:"Inter, sans-serif",fontSize:"0.85rem"}}>Record Again</button>
                    </div>
                  )}
                </div>
              )}

              {error && <div style={{background:"rgba(233,69,96,0.08)",padding:"14px",borderRadius:"10px",marginBottom:"15px",border:"1px solid rgba(233,69,96,0.2)"}}><p style={{color:"#e94560",margin:0,fontSize:"0.9rem"}}>⚠️ {error}</p></div>}

              <button onClick={handleAnalyze} disabled={!video || analyzing} style={{width:"100%",padding:"16px",background: !video||analyzing ? "rgba(255,255,255,0.05)" : "linear-gradient(135deg, #e94560, #ff6b35)",color: !video||analyzing ? "#444" : "white",border:"none",borderRadius:"12px",fontSize:"1rem",fontWeight:"700",fontFamily:"Inter, sans-serif",cursor: !video||analyzing ? "not-allowed" : "pointer",letterSpacing:"1px",textTransform:"uppercase"}}>
                {analyzing ? "Comparing..." : "Compare My Style to " + selectedFighter.name}
              </button>
            </>
          )}

          {analyzing && progressMsg && (
            <div style={{background:"rgba(255,255,255,0.02)",padding:"25px",borderRadius:"14px",textAlign:"center",border:"1px solid rgba(255,255,255,0.06)",marginTop:"15px"}}>
              <div style={{fontSize:"2rem",marginBottom:"10px"}}>🥊</div>
              <p style={{color:"#888",fontSize:"0.95rem",margin:0}}>{progressMsg}</p>
            </div>
          )}
        </>
      ) : (
        <div>
          <div style={{background:"linear-gradient(135deg, rgba(233,69,96,0.1), rgba(255,107,53,0.05))",border:"1px solid rgba(233,69,96,0.2)",borderRadius:"14px",padding:"16px 20px",marginBottom:"20px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <p style={{color:"#888",margin:"0 0 4px 0",fontSize:"0.75rem",textTransform:"uppercase",letterSpacing:"1px"}}>Compared to</p>
              <p style={{color:"white",margin:0,fontWeight:"700",fontSize:"1.1rem"}}>{selectedFighter.name}</p>
            </div>
            {getMatchPercentage(feedback) !== null && (
              <div style={{textAlign:"center"}}>
                <p style={{fontFamily:"Bebas Neue, sans-serif",fontSize:"2.5rem",margin:0,background:"linear-gradient(135deg, #e94560, #ff6b35)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",lineHeight:1}}>{getMatchPercentage(feedback)}%</p>
                <p style={{color:"#555",margin:"2px 0 0 0",fontSize:"0.75rem"}}>Style Match</p>
              </div>
            )}
          </div>

          <div style={{background:"rgba(255,255,255,0.02)",padding:"25px",borderRadius:"16px",border:"1px solid rgba(255,255,255,0.06)",marginBottom:"20px"}}>
            <div style={{color:"#ccc",lineHeight:"1.9",fontSize:"0.95rem"}}>{feedback.split("\n").map(function(line, i) { var clean = line.replace(/\*\*/g,"").replace(/^#+\s*/,"").replace(/^---$/,""); if (!clean.trim()) return <div key={i} style={{height:"8px"}}></div>; if (line.startsWith("#")) return <h3 key={i} style={{fontFamily:"Bebas Neue, sans-serif",fontSize:"1.3rem",letterSpacing:"1px",background:"linear-gradient(135deg, #e94560, #ff6b35)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",margin:"15px 0 8px 0"}}>{clean}</h3>; if (line.startsWith("✅") || line.startsWith("❌") || line.startsWith("🎯") || line.startsWith("🥊")) return <p key={i} style={{color:"white",fontWeight:"700",margin:"15px 0 8px 0",fontSize:"1rem"}}>{clean}</p>; if (line.startsWith("- ")) return <p key={i} style={{color:"#ccc",margin:"5px 0",paddingLeft:"15px",borderLeft:"2px solid rgba(233,69,96,0.3)"}}>{clean.substring(2)}</p>; return <p key={i} style={{color:"#ccc",margin:"5px 0"}}>{clean}</p>; })}</div>
          </div>

          <button onClick={function(){ setFeedback(null); setVideo(null); setPreviewUrl(null); setSelectedFighter(null); }} style={{width:"100%",padding:"14px",background:"rgba(255,255,255,0.03)",color:"#666",border:"1px solid rgba(255,255,255,0.08)",borderRadius:"12px",cursor:"pointer",fontFamily:"Inter, sans-serif",fontSize:"0.9rem"}}>
            Compare Again
          </button>
        </div>
      )}
    </div>
  );
}

export default StyleComparison;
